/**
 * What an employee asks for and a manager decides: offers, swaps, claims of open shifts,
 * and the employee's own absence, offer and availability requests.
 */
import { del, get, post, put } from '@/api/client';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { ApiAbsence } from './absence';
import type { ApiAvailability } from './availability';
import type { ApiUser } from './people';
import { type ApiShift, invalidateShifts } from './shifts';

export interface ApiOffer {
  id: number;
  shift_id: number;
  offered_by: number;
  accepted_by: number | null;
  status: string;
  employee_comment: string | null;
  offeredByUser: Pick<ApiUser, 'id' | 'name' | 'lastname'> | null;
  acceptedByUser: Pick<ApiUser, 'id' | 'name' | 'lastname'> | null;
  shift: ApiShift | null;
}
export interface ApiSwap {
  id: number;
  shift_id_one: number;
  shift_id_two: number;
  offered_by_one: number;
  offered_by_two: number;
  status: string;
  employee_comment: string | null;
  employee_two_comment: string | null;
  shiftOne: ApiShift | null;
  shiftTwo: ApiShift | null;
  offeredByUserOne: Pick<ApiUser, 'id' | 'name' | 'lastname'> | null;
  offeredByUserTwo: Pick<ApiUser, 'id' | 'name' | 'lastname'> | null;
}
/** GET /admin/shifts/pending-requests: offers and swaps in pending_manager_approval, absence requests pending. */
export function usePending(enabled = true) {
  return useQuery({
    queryKey: ['pending'],
    enabled,
    queryFn: () =>
      get<{
        pendingRequests: { shiftOffers: ApiOffer[]; shiftSwaps: ApiSwap[]; absenceRequests: ApiAbsence[] };
      }>('/admin/shifts/pending-requests'),
    staleTime: 30_000,
  });
}
export function useRequestWrites() {
  const qc = useQueryClient();
  // pending is among the keys invalidateShifts names
  const done = () => invalidateShifts(qc);
  return {
    approveOffer: useMutation({
      mutationFn: ({ id, comment }: { id: number; comment?: string }) =>
        put<unknown>(`/admin/shift-offers/${id}/approve`, { manager_comment: comment ?? '' }),
      onSettled: done,
    }),
    rejectOffer: useMutation({
      mutationFn: ({ id, comment }: { id: number; comment?: string }) =>
        post<unknown>(`/admin/shift-offers/${id}/reject`, { manager_comment: comment ?? '' }),
      onSettled: done,
    }),
    approveSwap: useMutation({
      mutationFn: ({ id, comment }: { id: number; comment?: string }) =>
        put<unknown>(`/admin/shift-swap-requests/${id}/approve`, { manager_comment: comment ?? '' }),
      onSettled: done,
    }),
    rejectSwap: useMutation({
      mutationFn: ({ id, comment }: { id: number; comment?: string }) =>
        post<unknown>(`/admin/shift-swap-requests/${id}/reject`, { manager_comment: comment ?? '' }),
      onSettled: done,
    }),
  };
}
/* ── open shifts the employee can ask for (plan 0019, claim) ─────────────── */
/** The admin date-range route answers a `user` with published shifts only; the open ones are those with no person. */
export function useOpenShifts(start: string, end: string, enabled = true) {
  return useQuery({
    enabled,
    queryKey: ['open-shifts', start, end],
    queryFn: async () =>
      (
        await get<{ shifts: ApiShift[] }>(`/admin/shifts/date-range?start_date=${start}&end_date=${end}`)
      ).shifts.filter((s) => s.user_id == null && s.published),
    staleTime: 30_000,
  });
}
export function useClaim() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, comment }: { id: number; comment?: string }) =>
      post<unknown>(`/shifts/${id}/claim`, { employee_comment: comment ?? null }),
    onSettled: () =>
      Promise.all([
        qc.invalidateQueries({ queryKey: ['open-shifts'] }),
        qc.invalidateQueries({ queryKey: ['pending'] }),
      ]),
  });
}
/* ── swaps, the employee's own side (TF-SWAP-CONTROLS) ─────────────────── */

/** The narrow projection `GET /shifts/swap-candidates` answers: no pay, no contact details. */
export interface ApiSwapCandidate {
  id: number;
  user_id: number;
  start_time: string;
  end_time: string;
  shiftType: { name: string; color_code: string } | null;
  user: { id: number; name: string; lastname: string | null } | null;
}
export function useSwapCandidates(start: string, end: string, enabled: boolean) {
  return useQuery({
    queryKey: ['swap-candidates', start, end],
    queryFn: () => get<ApiSwapCandidate[]>(`/shifts/swap-candidates?start_date=${start}&end_date=${end}`),
    staleTime: 30_000,
    enabled,
  });
}
/** The swaps this person is part of; the route answers a manager every swap and an employee their own. */
export function useMySwaps() {
  return useQuery({
    queryKey: ['my-swaps'],
    queryFn: () => get<ApiSwap[]>('/shift-swap-requests'),
    staleTime: 30_000,
  });
}
export function useSwapWrites() {
  const qc = useQueryClient();
  const done = () =>
    Promise.all([
      qc.invalidateQueries({ queryKey: ['my-swaps'] }),
      qc.invalidateQueries({ queryKey: ['my-shifts'] }),
      qc.invalidateQueries({ queryKey: ['swap-candidates'] }),
    ]);
  return {
    // the participants are derived on the server from the two shifts; nothing here names them
    propose: useMutation({
      mutationFn: (b: { shift_id_one: number; shift_id_two: number; employee_comment?: string | null }) =>
        post<ApiSwap>('/shift-swap-requests', b),
      onSettled: done,
    }),
    accept: useMutation({
      mutationFn: (id: number) => post<unknown>(`/shift-swap-requests/${id}/accept-by-employee-two`, {}),
      onSettled: done,
    }),
    decline: useMutation({
      mutationFn: (id: number) => post<unknown>(`/shift-swap-requests/${id}/decline-by-employee-two`, {}),
      onSettled: done,
    }),
  };
}
export function useMyWrites() {
  const qc = useQueryClient();
  return {
    requestAbsence: useMutation({
      mutationFn: (b: {
        start_date: string;
        end_date: string;
        reason?: string | null;
        absence_type_id: number;
      }) => post<ApiAbsence>('/absence-requests', b),
      onSettled: () => qc.invalidateQueries({ queryKey: ['my-absences'] }),
    }),
    withdrawAbsence: useMutation({
      mutationFn: (id: number) => del<unknown>(`/absence-requests/${id}`),
      onSettled: () => qc.invalidateQueries({ queryKey: ['my-absences'] }),
    }),
    /** an approved absence: asks the people who decide to cancel it; nothing on it changes */
    askCancel: useMutation({
      mutationFn: ({ id, reason }: { id: number; reason?: string }) =>
        post<unknown>(`/absence-requests/${id}/cancel-request`, reason ? { reason } : {}),
      onSettled: () => qc.invalidateQueries({ queryKey: ['work-time-changes'] }),
    }),
    offer: useMutation({
      mutationFn: (b: { shift_id: number; employee_comment?: string }) => post<unknown>('/shifts/offer', b),
      onSettled: () => qc.invalidateQueries({ queryKey: ['my-shifts'] }),
    }),
    revokeOffer: useMutation({
      mutationFn: (shift_id: number) => post<unknown>('/shifts/revoke-offer', { shift_id }),
      onSettled: () => qc.invalidateQueries({ queryKey: ['my-shifts'] }),
    }),
    addAvailability: useMutation({
      mutationFn: (b: {
        start_date_time: string;
        end_date_time: string;
        is_available: boolean;
        is_recurring: boolean;
        recurrence_pattern?: string | null;
        recurrence_end_date?: string | null;
      }) => post<ApiAvailability>('/availabilities', b),
      onSettled: () => qc.invalidateQueries({ queryKey: ['my-availabilities'] }),
    }),
    removeAvailability: useMutation({
      mutationFn: (id: number) => del<unknown>(`/availabilities/${id}`),
      onSettled: () => qc.invalidateQueries({ queryKey: ['my-availabilities'] }),
    }),
  };
}
